/**
 * End-of-leg coin review. Spiritual successor to the old AngularJS
 * `_coin_review.html` table, rebuilt as a card: final leg rankings across the
 * top, then a per-player row showing pyramid coins, leg-bet payouts (as
 * little betting slips), and the new wallet total. "Partnerships" column from
 * the old game is intentionally dropped (out of v1 scope).
 */

import { PLAYER_COLOR_HEX, type CamelUp } from '@mdev/common'
import { CAMEL_HEX, CU, CU_HEADING } from '../theme'
import { CamelGlyph } from './CamelGlyph'
import { Coins, Panel } from './pieces'

type CamelUpState = CamelUp.CamelUpState

interface Props {
  state: CamelUpState
}

function delta(n: number) {
  return n >= 0 ? `+${n}` : `${n}`
}

export function LegReviewPanel(props: Props) {
  const { state } = props
  const review = state.legReview
  if (!review) return null

  return (
    <Panel pad={20} style={{ boxShadow: CU.shadow }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontFamily: CU_HEADING, fontWeight: 900, fontSize: 24, color: CU.ink }}>
          End of Leg {review.leg}
        </span>
      </div>

      {/* Rankings */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 16,
          paddingBottom: 14,
          borderBottom: `1px solid ${CU.borderSoft}`,
        }}
      >
        {review.rankings.map((c, i) => (
          <div
            key={c}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px 4px 6px',
              borderRadius: 999,
              background: CU.surfaceSolid,
              border: `1px solid ${i === 0 ? CU.brass : CU.borderSoft}`,
            }}
          >
            <span style={{ fontFamily: CU_HEADING, fontWeight: 900, color: i === 0 ? CU.brass : CU.inkSoft }}>
              {i + 1}
            </span>
            <CamelGlyph color={c} size={20} noShadow />
          </div>
        ))}
      </div>

      {/* Player rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {review.rows.map((row) => {
          const p = state.players[row.uid]
          const net = row.pyramidCoins + row.legBetCoins
          return (
            <div
              key={row.uid}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '10px 12px',
                borderRadius: 10,
                background: CU.surfaceSolid,
                borderLeft: `4px solid ${PLAYER_COLOR_HEX[p.color]}`,
              }}
            >
              {/* Top line: name (left) · net + end coins (right). Stays on one
                  line at any width so the wallet total never clips. */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 18 }}>{p.avatar}</span>
                  <span style={{ fontFamily: CU_HEADING, fontWeight: 600, color: CU.ink }}>{p.displayName}</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 auto' }}>
                  <span style={{ fontFamily: CU_HEADING, fontWeight: 700, color: net >= 0 ? '#1DA64A' : CU.accent }}>
                    {delta(net)}
                  </span>
                  <Coins amount={row.endCoins} />
                </span>
              </div>

              {/* Breakdown chips wrap freely below. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <Pill label={`pyramid ${delta(row.pyramidCoins)}`} />
                {row.legBetBreakdown.map((b, i) => (
                  <span
                    key={i}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontFamily: CU_HEADING,
                      color: CAMEL_HEX[b.color].ink,
                      background: `linear-gradient(135deg, ${CAMEL_HEX[b.color].light}, ${CAMEL_HEX[b.color].dark})`,
                    }}
                  >
                    {b.color} {b.value} · {delta(b.payout)}
                  </span>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </Panel>
  )
}

function Pill({ label }: { label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 12,
        fontFamily: CU_HEADING,
        color: CU.brass,
        background: 'transparent',
        border: `1px solid ${CU.brass}`,
      }}
    >
      {label}
    </span>
  )
}
